/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { ReactNode, HTMLAttributes, forwardRef, Ref, useContext } from 'react';
import { classNames } from 'primereact/utils';
import StickyBox from 'react-sticky-box';
import type { StickyBoxConfig } from 'react-sticky-box';
import { universalRenderer } from 'utils/universalRenderer';
import { BoxLayoutContext } from './Box';
import './Box.scss';

export type BoxSectionHeader = ReactNode | (() => ReactNode);

export type BoxSectionProps = Pick<HTMLAttributes<HTMLDivElement>, 'onClick' | 'style' | 'className' | 'children'> & {
	header?: BoxSectionHeader;
	headerClassName?: string;
	contentClassName?: string;
	noPad?: boolean;
	horizontal?: boolean;
	sticky?: boolean | StickyBoxConfig;
};

export const BoxSection = forwardRef(
	(
		{
			header,
			headerClassName,
			contentClassName,
			children,
			className,
			noPad = false,
			horizontal = false,
			sticky = false,
			...restProps
		}: BoxSectionProps,
		ref: Ref<HTMLDivElement>
	) => {
		const { isVertical } = useContext(BoxLayoutContext);
		const stickyConfig: StickyBoxConfig = sticky === true ? { offsetTop: 16 } : sticky || {};
		const shouldUseSticky = Boolean(sticky) && isVertical;
		const content = (
			<div
				className={classNames(
					'box-section-content',
					{ 'box-section-pad': !noPad, 'box-section-horizontal': horizontal },
					contentClassName
				)}
			>
				{children}
			</div>
		);

		if (shouldUseSticky) {
			return (
				<StickyBox className={classNames('box-section', 'box-section-sticky', className)} {...stickyConfig}>
					<div ref={ref} onClick={restProps.onClick} style={restProps.style}>
						{header && (
							<h3 className={classNames('box-section-header', headerClassName)}>
								{universalRenderer(header)}
							</h3>
						)}
						{content}
					</div>
				</StickyBox>
			);
		}

		return (
			<div ref={ref} className={classNames('box-section', className)} {...restProps}>
				{header && (
					<h3 className={classNames('box-section-header', headerClassName)}>{universalRenderer(header)}</h3>
				)}
				{content}
			</div>
		);
	}
);
